// src/MuhtarDashboard.js
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { API_BASE } from "./config";

/* ---------- Basit i18n (yapıyı bozmadan) ---------- */
const I18N = {
  tr: {
    adminPanel: "Admin Paneli",
    dashboardSub:
      "Kurum yönetimi, finans, stok, müşteri ve sipariş durumunu tek ekrandan takip edin.",
    donations: "Ana Kategoriler",
    users: "Kullanıcılar",
    announcements: "Duyurular",
    events: "Etkinlikler",
    news: "Geri Bildirim",
    activities: "Faaliyetler",
    recentAnnouncements: "📢 Son Duyurular",
    upcomingEvents: "📆 Yaklaşan Etkinlikler",
    detail: "Detay ↗",
    addAnnouncement: "➕ Duyuru Ekle",
    createEvent: "📅 Etkinlik Oluştur",
    settings: "Ayarlar",
    support: "Canlı Destek",
    logout: "Çıkış Yap",
    activePanel: "Panel Aktif",
    businessStatus: "İşletme Durumu",
    quickModules: "Hızlı Modül Geçişleri",
    cash: "Kasa",
    todayCash: "Bugünkü Kasa",
    monthCash: "Bu Ay Kasa",
    current: "Cari",
    stock: "Stok",
    orders: "Siparişler",
    totalDebt: "Toplam Borç",
    totalReceivable: "Toplam Alacak",
    debtorCustomer: "Borçlu Cari",
    stockCards: "Stok Kartı",
    criticalStock: "Kritik Stok",
    totalOrders: "Toplam Sipariş",
    pendingOrders: "Bekleyen Sipariş",
    loading: "Yükleniyor",
    liveSummary: "Canlı Özet",
    appMenu: "Uygulama Menüsü",
    cashRecords: "Kasa Kaydı",
    safeExit: "Güvenli Çıkış",
  },
  en: {
    adminPanel: "Admin Panel",
    dashboardSub:
      "Track finance, stock, customers and orders from one professional dashboard.",
    donations: "Main Categories",
    users: "Users",
    announcements: "Announcements",
    events: "Events",
    news: "Feedback",
    activities: "Activities",
    recentAnnouncements: "📢 Latest Announcements",
    upcomingEvents: "📆 Upcoming Events",
    detail: "Details ↗",
    addAnnouncement: "➕ Add Announcement",
    createEvent: "📅 Create Event",
    settings: "Settings",
    support: "Live Support",
    logout: "Logout",
    activePanel: "Panel Active",
    businessStatus: "Business Status",
    quickModules: "Quick Module Access",
    cash: "Cash",
    todayCash: "Today Cash",
    monthCash: "Monthly Cash",
    current: "Customers",
    stock: "Stock",
    orders: "Orders",
    totalDebt: "Total Debt",
    totalReceivable: "Receivable",
    debtorCustomer: "Debtors",
    stockCards: "Stock Cards",
    criticalStock: "Critical Stock",
    totalOrders: "Total Orders",
    pendingOrders: "Pending Orders",
    loading: "Loading",
    liveSummary: "Live Summary",
    appMenu: "App Menu",
    cashRecords: "Cash Records",
    safeExit: "Safe Exit",
  },
  de: {
    adminPanel: "Admin-Panel",
    dashboardSub:
      "Finanzen, Lager, Kunden und Bestellungen professionell auf einem Bildschirm verfolgen.",
    donations: "Hauptkategorien",
    users: "Benutzer",
    announcements: "Ankündigungen",
    events: "Veranstaltungen",
    news: "Feedback",
    activities: "Aktivitäten",
    recentAnnouncements: "📢 Neueste Ankündigungen",
    upcomingEvents: "📆 Bevorstehende Veranstaltungen",
    detail: "Details ↗",
    addAnnouncement: "➕ Ankündigung hinzufügen",
    createEvent: "📅 Veranstaltung erstellen",
    settings: "Einstellungen",
    support: "Live-Support",
    logout: "Abmelden",
    activePanel: "Panel Aktiv",
    businessStatus: "Betriebsstatus",
    quickModules: "Schneller Modulzugriff",
    cash: "Kasse",
    todayCash: "Heute Kasse",
    monthCash: "Monat Kasse",
    current: "Kunden",
    stock: "Lager",
    orders: "Bestellungen",
    totalDebt: "Schulden",
    totalReceivable: "Forderungen",
    debtorCustomer: "Schuldner",
    stockCards: "Lagerkarten",
    criticalStock: "Kritisch",
    totalOrders: "Bestellungen",
    pendingOrders: "Offen",
    loading: "Laden",
    liveSummary: "Live-Übersicht",
    appMenu: "App-Menü",
    cashRecords: "Kassenbelege",
    safeExit: "Sicher abmelden",
  },
};

const getLang = () => {
  const fromPanel = (localStorage.getItem("panel_dili") || "").toLowerCase();
  const fromApp = (localStorage.getItem("app_lang") || "").toLowerCase();
  const fromLang = (localStorage.getItem("lang") || "").toLowerCase();
  const nav = ((navigator.language || "tr").slice(0, 2) || "tr").toLowerCase();
  const candidate = fromPanel || fromApp || fromLang || nav;
  return ["tr", "en", "de"].includes(candidate) ? candidate : "tr";
};

const localeToDate = { tr: "tr-TR", en: "en-US", de: "de-DE" };

const tokenHeader = () => {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("accessToken") ||
    "";

  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const normalizeList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  const cleaned = String(value)
    .replace(/[^\d,.-]/g, "")
    .replace(/\.(?=\d{3}(\D|$))/g, "")
    .replace(",", ".");

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const pickNumber = (obj, keys) => {
  for (const key of keys) {
    if (obj && obj[key] !== undefined && obj[key] !== null && obj[key] !== "") {
      return toNumber(obj[key]);
    }
  }
  return 0;
};

const getDateOnly = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const isToday = (value) => {
  const today = new Date().toISOString().slice(0, 10);
  return getDateOnly(value) === today;
};

const isThisMonth = (value) => {
  const month = new Date().toISOString().slice(0, 7);
  return getDateOnly(value).slice(0, 7) === month;
};

const getCustomerFinance = (customer = {}) => {
  const debt = Math.max(
    toNumber(customer.debt_amount),
    toNumber(customer.remaining_debt),
    toNumber(customer.total_debt),
    toNumber(customer.borc),
    toNumber(customer.borc_tutari),
    toNumber(customer.debit_total)
  );

  const receivable = Math.max(
    toNumber(customer.receivable_amount),
    toNumber(customer.credit_amount),
    toNumber(customer.total_receivable),
    toNumber(customer.alacak),
    toNumber(customer.alacak_tutari),
    toNumber(customer.credit_total)
  );

  const hasFinanceTotals = debt > 0 || receivable > 0;

  const rawBalance =
    customer.balance_amount ?? customer.net_balance ?? customer.bakiye ?? 0;

  const netBalance = hasFinanceTotals ? debt - receivable : toNumber(rawBalance);

  return {
    debt,
    receivable,
    netBalance,
  };
};

const playPanelSound = (type = "nav") => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;

    const ctx = new AudioContext();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();

    const frequency =
      type === "logout" ? 260 : type === "support" ? 660 : 520;

    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);

    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.045, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.08);

    oscillator.connect(gain);
    gain.connect(ctx.destination);

    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.09);
  } catch {
    // Tarayıcı ses izni vermezse sessiz geçer.
  }
};

const triggerRouteAnimation = () => {
  try {
    document.documentElement.classList.remove("muhtar-route-transitioning");
    void document.documentElement.offsetWidth;
    document.documentElement.classList.add("muhtar-route-transitioning");

    window.setTimeout(() => {
      document.documentElement.classList.remove("muhtar-route-transitioning");
    }, 320);
  } catch {
    // Sessiz geç.
  }
};

const MuhtarDashboard = () => {
  const token = localStorage.getItem("token");

  const [duyurular, setDuyurular] = useState([]);
  const [etkinlikler, setEtkinlikler] = useState([]);
  const [anakategoriler, setAnaKategoriler] = useState([]);
  const [geribildirim, setGeriBildirim] = useState([]);
  const [kullanicilar, setKullanicilar] = useState([]);
  const [faaliyetler, setFaaliyetler] = useState([]);

  const [dashboardLoading, setDashboardLoading] = useState(false);

  const [metrics, setMetrics] = useState({
    kasaToplam: 0,
    kasaBugun: 0,
    kasaAy: 0,
    kasaKayit: 0,

    cariToplamBorc: 0,
    cariToplamAlacak: 0,
    cariNet: 0,
    cariBorclu: 0,
    cariKayit: 0,

    stokKart: 0,
    kritikStok: 0,
    stokDeger: 0,

    siparisToplam: 0,
    siparisBekleyen: 0,
    siparisBugun: 0,
    siparisCiro: 0,
  });

  const [lang, setLang] = useState(getLang());
  const t = (key) => (I18N[lang] && I18N[lang][key]) || key;
  const dateLocale = localeToDate[lang] || "tr-TR";

  const moneyFormatter = useMemo(() => {
    return new Intl.NumberFormat(dateLocale, {
      style: "currency",
      currency: "TRY",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [dateLocale]);

  const formatMoney = (value) => moneyFormatter.format(toNumber(value));

  const handleNavigate = (hash) => {
    playPanelSound("nav");
    triggerRouteAnimation();

    window.setTimeout(() => {
      window.location.hash = hash;
    }, 70);
  };

  const openWhatsAppSupport = () => {
    playPanelSound("support");

    const message = encodeURIComponent(
      "Merhaba, MesYat Dijital panel için canlı destek almak istiyorum."
    );

    window.open(`https://wa.me/905537027474?text=${message}`, "_blank");
  };

  const handleLogout = () => {
    playPanelSound("logout");
    triggerRouteAnimation();

    window.setTimeout(() => {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("kurum_id");
      localStorage.removeItem("kurum");
      localStorage.removeItem("rol");
      localStorage.removeItem("authToken");
      localStorage.removeItem("accessToken");

      window.location.href = "/login";
    }, 130);
  };

  const loadSimplePanelData = async () => {
    const headers = tokenHeader();

    fetch(`${API_BASE}/duyurular`, { headers })
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];
        setDuyurular(list);
      })
      .catch(() => setDuyurular([]));

    fetch(`${API_BASE}/etkinlikler`, { headers })
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];
        setEtkinlikler(list);
      })
      .catch(() => setEtkinlikler([]));

    fetch(`${API_BASE}/anakategoriler`, { headers })
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data?.data) ? data.data : [];
        setAnaKategoriler(list);
      })
      .catch(() => setAnaKategoriler([]));

    fetch(`${API_BASE}/geribildirimler`, { headers })
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];
        setGeriBildirim(list);
      })
      .catch(() => setGeriBildirim([]));

    fetch(`${API_BASE}/kullanicilar`, { headers })
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data?.data) ? data.data : [];
        setKullanicilar(list);
      })
      .catch(() => setKullanicilar([]));

    fetch(`${API_BASE}/faaliyetler`, { headers })
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data?.data) ? data.data : [];
        setFaaliyetler(list);
      })
      .catch(() => setFaaliyetler([]));
  };

  const loadBusinessMetrics = async () => {
    const headers = tokenHeader();

    setDashboardLoading(true);

    try {
      const [
        cashListRes,
        cashSummaryRes,
        cariCustomersRes,
        cariSummaryRes,
        stockItemsRes,
        stockMovementsRes,
        ordersRes,
      ] = await Promise.allSettled([
        fetch(`${API_BASE}/cash`, { headers }),
        fetch(`${API_BASE}/cash/reports/summary`, { headers }),
        fetch(`${API_BASE}/cari/customers?limit=500&offset=0`, { headers }),
        fetch(`${API_BASE}/cari/reports/summary`, { headers }),
        fetch(`${API_BASE}/stock/items`, { headers }),
        fetch(`${API_BASE}/stock/movements`, { headers }),
        fetch(`${API_BASE}/qr-orders`, { headers }),
      ]);

      const cashJson =
        cashListRes.status === "fulfilled"
          ? await cashListRes.value.json().catch(() => ({}))
          : {};

      const cashSummaryJson =
        cashSummaryRes.status === "fulfilled"
          ? await cashSummaryRes.value.json().catch(() => ({}))
          : {};

      const cariJson =
        cariCustomersRes.status === "fulfilled"
          ? await cariCustomersRes.value.json().catch(() => ({}))
          : {};

      const cariSummaryJson =
        cariSummaryRes.status === "fulfilled"
          ? await cariSummaryRes.value.json().catch(() => ({}))
          : {};

      const stockJson =
        stockItemsRes.status === "fulfilled"
          ? await stockItemsRes.value.json().catch(() => ({}))
          : {};

      const stockMovementsJson =
        stockMovementsRes.status === "fulfilled"
          ? await stockMovementsRes.value.json().catch(() => ({}))
          : {};

      const ordersJson =
        ordersRes.status === "fulfilled"
          ? await ordersRes.value.json().catch(() => ({}))
          : {};

      const cashRows = normalizeList(cashJson);
      const cashSummary = cashSummaryJson?.data?.summary || cashSummaryJson?.data || {};

      const incomeAmount = (row) => {
        const amount = pickNumber(row, [
          "total_amount",
          "total",
          "amount",
          "tutar",
          "toplam",
          "odeme_tutari",
          "payment_amount",
          "net_tutar",
          "unit_price",
        ]);

        const type = String(
          row.transaction_type || row.type || row.tip || row.tur || ""
        ).toLowerCase();

        const isExpense =
          type.includes("expense") ||
          type.includes("gider") ||
          type.includes("çıkış") ||
          type.includes("cikis");

        return isExpense ? -amount : amount;
      };

      const kasaToplamFromRows = cashRows.reduce(
        (sum, row) => sum + incomeAmount(row),
        0
      );

      const kasaBugunFromRows = cashRows
        .filter((row) =>
          isToday(row.transaction_date || row.date || row.created_at || row.tarih)
        )
        .reduce((sum, row) => sum + incomeAmount(row), 0);

      const kasaAyFromRows = cashRows
        .filter((row) =>
          isThisMonth(row.transaction_date || row.date || row.created_at || row.tarih)
        )
        .reduce((sum, row) => sum + incomeAmount(row), 0);

      const kasaToplam =
        pickNumber(cashSummary, [
          "total",
          "net_total",
          "total_amount",
          "income_total",
          "month_total",
          "monthly_total",
        ]) || kasaToplamFromRows;

      const kasaBugun =
        pickNumber(cashSummary, [
          "today",
          "today_total",
          "daily_total",
          "today_income",
        ]) || kasaBugunFromRows;

      const kasaAy =
        pickNumber(cashSummary, [
          "month",
          "month_total",
          "monthly_total",
          "monthly_income",
        ]) || kasaAyFromRows;

      let cariRows = normalizeList(cariJson);
      const cariSummary = cariSummaryJson?.data || {};
      const cariTotals = cariSummary?.totals || cariSummary || {};

      const shouldHydrateCari =
        cariRows.length > 0 &&
        !cariRows.some((c) => {
          const f = getCustomerFinance(c);
          return f.debt > 0 || f.receivable > 0 || f.netBalance !== 0;
        });

      if (shouldHydrateCari) {
        const hydrated = [];
        const chunkSize = 20;

        for (let i = 0; i < cariRows.length; i += chunkSize) {
          const chunk = cariRows.slice(i, i + chunkSize);

          const enrichedChunk = await Promise.all(
            chunk.map(async (customer) => {
              try {
                const res = await fetch(
                  `${API_BASE}/cari/customers/${customer.id}/account`,
                  { headers }
                );

                const json = await res.json().catch(() => ({}));
                const account = json.data || {};
                const finance = account.finance || {};

                return {
                  ...customer,
                  debt_amount:
                    finance.debt_amount ??
                    finance.debt ??
                    finance.total_debt ??
                    customer.debt_amount ??
                    customer.remaining_debt ??
                    customer.total_debt ??
                    0,
                  receivable_amount:
                    finance.receivable_amount ??
                    finance.receivable ??
                    finance.total_receivable ??
                    customer.receivable_amount ??
                    customer.credit_amount ??
                    customer.total_receivable ??
                    0,
                  balance_amount:
                    finance.balance_amount ??
                    finance.net_balance ??
                    finance.balance ??
                    customer.balance_amount ??
                    customer.net_balance ??
                    customer.bakiye ??
                    undefined,
                };
              } catch {
                return customer;
              }
            })
          );

          hydrated.push(...enrichedChunk);
        }

        cariRows = hydrated;
      }

      const cariCalculated = cariRows.reduce(
        (acc, customer) => {
          const finance = getCustomerFinance(customer);

          acc.totalDebt += finance.debt;
          acc.totalReceivable += finance.receivable;
          acc.netBalance += finance.netBalance;

          if (finance.netBalance > 0) acc.debtorCount += 1;

          return acc;
        },
        {
          totalDebt: 0,
          totalReceivable: 0,
          netBalance: 0,
          debtorCount: 0,
        }
      );

      const cariToplamBorc =
        pickNumber(cariTotals, [
          "totalDebt",
          "total_debt",
          "debt_total",
          "borc",
          "total_borc",
          "total_borç",
        ]) || cariCalculated.totalDebt;

      const cariToplamAlacak =
        pickNumber(cariTotals, [
          "totalReceivable",
          "total_receivable",
          "receivable_total",
          "alacak",
          "total_alacak",
        ]) || cariCalculated.totalReceivable;

      const cariBorclu =
        pickNumber(cariTotals, [
          "debtorCount",
          "debtor_count",
          "borclu_count",
          "borçlu_count",
        ]) || cariCalculated.debtorCount;

      const stockRows = normalizeList(stockJson);
      const stockMovements = normalizeList(stockMovementsJson);

      const kritikStok = stockRows.filter((item) => {
        const current = pickNumber(item, [
          "current_stock",
          "mevcut_stok",
          "stock",
          "stok",
          "quantity",
          "miktar",
          "adet",
        ]);

        const min = pickNumber(item, [
          "min_stock",
          "min_stok",
          "minimum_stock",
          "minimum_stok",
          "critical_stock",
          "kritik_stok",
        ]);

        return min > 0 && current <= min;
      }).length;

      const stokDeger = stockRows.reduce((sum, item) => {
        const current = pickNumber(item, [
          "current_stock",
          "mevcut_stok",
          "stock",
          "stok",
          "quantity",
          "miktar",
          "adet",
        ]);

        const purchase = pickNumber(item, [
          "purchase_price",
          "alis_fiyati",
          "alış_fiyatı",
          "cost",
          "maliyet",
        ]);

        return sum + current * purchase;
      }, 0);

      const ordersRows = normalizeList(ordersJson);

      const pendingOrders = ordersRows.filter((order) => {
        const durum = String(order.durum || order.status || "").toLowerCase();

        return (
          durum.includes("yeni") ||
          durum.includes("new") ||
          durum.includes("bek") ||
          durum.includes("pending") ||
          durum.includes("hazirlaniyor") ||
          durum.includes("hazırlanıyor") ||
          durum.includes("preparing")
        );
      }).length;

      const todayOrders = ordersRows.filter((order) =>
        isToday(order.created_at || order.createdAt || order.tarih || order.date)
      ).length;

      const orderRevenue = ordersRows.reduce((sum, order) => {
        const durum = String(order.durum || order.status || "").toLowerCase();

        if (durum.includes("iptal") || durum.includes("cancel")) return sum;

        return (
          sum +
          pickNumber(order, [
            "toplam_tutar",
            "total_amount",
            "total",
            "amount",
            "tutar",
          ])
        );
      }, 0);

      setMetrics({
        kasaToplam,
        kasaBugun,
        kasaAy,
        kasaKayit: cashRows.length,

        cariToplamBorc,
        cariToplamAlacak,
        cariNet: cariToplamBorc - cariToplamAlacak,
        cariBorclu,
        cariKayit: cariRows.length,

        stokKart: stockRows.length,
        kritikStok,
        stokDeger,

        siparisToplam: ordersRows.length,
        siparisBekleyen: pendingOrders,
        siparisBugun: todayOrders,
        siparisCiro: orderRevenue,
      });
    } catch (err) {
      console.error("Dashboard metrikleri alınamadı:", err);
    } finally {
      setDashboardLoading(false);
    }
  };

  useEffect(() => {
    loadSimplePanelData();
    loadBusinessMetrics();

    const onStorage = (e) => {
      if (e.key === "app_lang" || e.key === "lang" || e.key === "panel_dili") {
        setLang(getLang());
      }
    };

    const onAppLangChange = () => setLang(getLang());

    window.addEventListener("storage", onStorage);
    window.addEventListener("app_lang_change", onAppLangChange);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("app_lang_change", onAppLangChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDuyurular([]);
    setEtkinlikler([]);
    setAnaKategoriler([]);
    setGeriBildirim([]);
    setKullanicilar([]);
    setFaaliyetler([]);

    loadSimplePanelData();
    loadBusinessMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const onHashChange = () => {
      triggerRouteAnimation();
    };

    const onDocumentClick = (event) => {
      const target = event.target;
      if (!target || !target.closest) return;

      const link = target.closest('a[href^="#/"]');
      const dashboardButton = target.closest('[data-muhtar-nav="true"]');

      if (link || dashboardButton) {
        playPanelSound("nav");
        triggerRouteAnimation();
      }
    };

    window.addEventListener("hashchange", onHashChange);
    document.addEventListener("click", onDocumentClick, true);

    return () => {
      window.removeEventListener("hashchange", onHashChange);
      document.removeEventListener("click", onDocumentClick, true);
    };
  }, []);

  const DashboardCard = ({ count, label, color, href, delay = 0, icon = "◆" }) => (
    <div
      className={`muhtar-dashboard-card ${color} text-white rounded-lg p-6 shadow-md`}
      style={{ animationDelay: `${delay}ms` }}
      onClick={() => handleNavigate(href)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter") handleNavigate(href);
      }}
    >
      <div className="muhtar-dashboard-card-glow" />

      <div className="muhtar-dashboard-card-top">
        <div className="muhtar-dashboard-icon">{icon}</div>
        <span className="muhtar-dashboard-status-dot" />
      </div>

      <div className="muhtar-dashboard-count text-3xl font-bold">{count}</div>
      <div className="muhtar-dashboard-label text-sm mt-2">{label}</div>

      <a
        href={href}
        className="muhtar-dashboard-detail mt-3 text-xs underline cursor-pointer block"
        onClick={(event) => {
          event.preventDefault();
          handleNavigate(href);
        }}
      >
        {t("detail")}
      </a>
    </div>
  );

  const MetricCard = ({ title, value, note, tone = "slate", delay = 0 }) => (
    <div
      className={`muhtar-metric-card muhtar-metric-${tone}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="muhtar-metric-title">{title}</div>
      <div className="muhtar-metric-value">
        {dashboardLoading ? t("loading") : value}
      </div>
      <div className="muhtar-metric-note">{note}</div>
    </div>
  );

  const quickModules = [
    {
      title: "Menü Yönetimi",
      desc: "Kategori, ürün, fiyat ve QR menü içerikleri",
      href: "#/anakategoriler",
      meta: `${anakategoriler.length} kategori`,
      icon: "▦",
    },
    {
      title: "Siparişler",
      desc: "Masa siparişleri ve garson çağrıları",
      href: "#/siparisler",
      meta: `${metrics.siparisBekleyen} bekleyen`,
      icon: "▣",
    },
    {
      title: "Kasa & Tahsilat",
      desc: "Gelir, gider, tahsilat ve kasa raporları",
      href: "#/kasa-tahsilat",
      meta: formatMoney(metrics.kasaToplam),
      icon: "₺",
    },
    {
      title: "Cari / Müşteriler",
      desc: "Borç, alacak ve müşteri bakiyeleri",
      href: "#/cari-musteriler",
      meta: `${metrics.cariBorclu} borçlu`,
      icon: "◉",
    },
    {
      title: "Stok Yönetimi",
      desc: "Stok kartları, kritik stok ve hareketler",
      href: "#/stok-yonetimi",
      meta: `${metrics.kritikStok} kritik`,
      icon: "□",
    },
    {
      title: "Entegrasyonlar",
      desc: "Yemeksepeti, POS, WhatsApp ve dış bağlantılar",
      href: "#/entegrasyonlar",
      meta: "Bağlantılar",
      icon: "↔",
    },
    {
      title: "Kuaför & Güzellik",
      desc: "Randevu, seans, paket ve müşteri takibi",
      href: "#/beauty",
      meta: "Beauty Pro",
      icon: "✂",
    },
    {
      title: "Canlı Destek",
      desc: "WhatsApp üzerinden teknik destek alın",
      href: "support",
      meta: "WhatsApp",
      icon: "☎",
      support: true,
    },
  ];

  return (
    <div className="muhtar-dashboard-page ml-[10px] p-8 overflow-y-auto">
      <style>{`
        html.muhtar-route-transitioning .muhtar-dashboard-app-shell {
          animation: muhtarRouteChange .26s ease both;
        }

        .muhtar-dashboard-page {
          min-height: 100vh;
          position: relative;
          overflow-x: hidden;
          background:
            radial-gradient(circle at 0% 0%, rgba(249, 115, 22, .14), transparent 34%),
            radial-gradient(circle at 100% 0%, rgba(37, 99, 235, .13), transparent 34%),
            linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
          animation: muhtarPageFade .45s ease both;
        }

        .muhtar-dashboard-page::before,
        .muhtar-dashboard-page::after {
          content: "";
          position: fixed;
          width: 280px;
          height: 280px;
          border-radius: 999px;
          pointer-events: none;
          z-index: 0;
          filter: blur(8px);
          opacity: .6;
          animation: muhtarOrbFloat 7s ease-in-out infinite alternate;
        }

        .muhtar-dashboard-page::before {
          top: 80px;
          left: 30px;
          background: radial-gradient(circle, rgba(249, 115, 22, .16), transparent 68%);
        }

        .muhtar-dashboard-page::after {
          right: 60px;
          bottom: 90px;
          background: radial-gradient(circle, rgba(37, 99, 235, .14), transparent 68%);
          animation-delay: .8s;
        }

        .muhtar-dashboard-page > * {
          position: relative;
          z-index: 1;
        }

        .muhtar-dashboard-app-shell {
          width: 100%;
        }

        .muhtar-dashboard-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 18px;
          margin-bottom: 24px;
          animation: muhtarTitleIn .55s ease both;
        }

        .muhtar-dashboard-title {
          letter-spacing: -.03em;
          color: #0f172a;
        }

        .muhtar-dashboard-subtitle {
          margin-top: 8px;
          color: #64748b;
          font-size: 14px;
          line-height: 1.5;
        }

        .muhtar-dashboard-header-right {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .muhtar-dashboard-live-pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          border-radius: 999px;
          background: rgba(15, 23, 42, .92);
          color: white;
          font-size: 12px;
          font-weight: 900;
          box-shadow: 0 16px 38px rgba(15, 23, 42, .18);
          white-space: nowrap;
        }

        .muhtar-dashboard-live-pill span {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: #22c55e;
          box-shadow: 0 0 0 7px rgba(34, 197, 94, .16);
          animation: muhtarPulse 1.7s ease-in-out infinite;
        }

        .muhtar-header-action {
          border: 0;
          min-height: 42px;
          padding: 0 16px;
          border-radius: 999px;
          color: white;
          font-size: 13px;
          font-weight: 950;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: transform .16s ease, box-shadow .16s ease, filter .16s ease;
          box-shadow: 0 14px 32px rgba(15, 23, 42, .16);
          white-space: nowrap;
        }

        .muhtar-header-action:hover {
          transform: translateY(-2px);
          filter: brightness(1.04);
        }

        .muhtar-header-settings {
          background: linear-gradient(135deg, #475569, #334155);
        }

        .muhtar-header-support {
          background: linear-gradient(135deg, #16a34a, #15803d);
        }

        .muhtar-header-logout {
          background: linear-gradient(135deg, #ef4444, #dc2626);
        }

        .muhtar-section-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 16px;
        }

        .muhtar-section-title {
          animation: muhtarSoftIn .55s ease both;
          color: #0f172a;
          letter-spacing: -.02em;
          margin: 0;
        }

        .muhtar-section-chip {
          font-size: 12px;
          font-weight: 950;
          color: #475569;
          background: rgba(255,255,255,.72);
          border: 1px solid rgba(226,232,240,.95);
          padding: 8px 10px;
          border-radius: 999px;
          white-space: nowrap;
        }

        .muhtar-metrics-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 14px;
          margin-bottom: 28px;
        }

        .muhtar-metric-card {
          border-radius: 20px;
          padding: 18px;
          border: 1px solid rgba(226, 232, 240, .95);
          background: rgba(255,255,255,.82);
          box-shadow: 0 12px 30px rgba(15, 23, 42, .08);
          opacity: 0;
          transform: translateY(12px);
          animation: muhtarListIn .46s ease forwards;
          position: relative;
          overflow: hidden;
        }

        .muhtar-metric-card::after {
          content: "";
          position: absolute;
          right: -36px;
          top: -36px;
          width: 98px;
          height: 98px;
          border-radius: 999px;
          opacity: .18;
        }

        .muhtar-metric-orange::after { background: #f97316; }
        .muhtar-metric-blue::after { background: #2563eb; }
        .muhtar-metric-green::after { background: #22c55e; }
        .muhtar-metric-red::after { background: #ef4444; }
        .muhtar-metric-purple::after { background: #7c3aed; }
        .muhtar-metric-slate::after { background: #334155; }

        .muhtar-metric-title {
          font-size: 12px;
          font-weight: 950;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: .04em;
        }

        .muhtar-metric-value {
          margin-top: 8px;
          font-size: 22px;
          line-height: 1.1;
          font-weight: 950;
          color: #0f172a;
          letter-spacing: -.04em;
          min-height: 25px;
        }

        .muhtar-metric-note {
          margin-top: 7px;
          font-size: 12px;
          font-weight: 800;
          color: #64748b;
        }

        .muhtar-module-list {
          list-style: none;
          padding: 0;
          margin: 0 0 30px;
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 14px;
        }

        .muhtar-module-list li {
          min-width: 0;
        }

        .muhtar-module-button {
          width: 100%;
          min-height: 92px;
          border: 1px solid rgba(226, 232, 240, .98);
          border-radius: 22px;
          background: rgba(255,255,255,.84);
          box-shadow: 0 12px 28px rgba(15, 23, 42, .08);
          display: grid;
          grid-template-columns: 48px 1fr auto;
          gap: 14px;
          align-items: center;
          text-align: left;
          padding: 16px;
          cursor: pointer;
          opacity: 0;
          transform: translateY(12px);
          animation: muhtarListIn .5s ease forwards;
          transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
        }

        .muhtar-module-button:hover {
          transform: translateY(-3px);
          box-shadow: 0 18px 38px rgba(15, 23, 42, .13);
          border-color: rgba(249, 115, 22, .42);
        }

        .muhtar-module-button:active {
          transform: scale(.99);
        }

        .muhtar-module-icon {
          width: 48px;
          height: 48px;
          border-radius: 18px;
          background: linear-gradient(135deg, #0f172a, #334155);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          font-weight: 950;
          box-shadow: 0 12px 24px rgba(15, 23, 42, .18);
        }

        .muhtar-module-title {
          display: block;
          font-size: 15px;
          font-weight: 950;
          color: #0f172a;
          line-height: 1.2;
        }

        .muhtar-module-desc {
          display: block;
          margin-top: 4px;
          font-size: 12px;
          font-weight: 700;
          color: #64748b;
          line-height: 1.35;
        }

        .muhtar-module-meta {
          align-self: center;
          white-space: nowrap;
          font-size: 11px;
          font-weight: 950;
          color: #ea580c;
          background: rgba(249, 115, 22, .10);
          border: 1px solid rgba(249, 115, 22, .18);
          padding: 8px 9px;
          border-radius: 999px;
        }

        .muhtar-dashboard-card {
          position: relative;
          overflow: hidden;
          transform: translateY(16px) scale(.985);
          opacity: 0;
          animation: muhtarCardIn .58s cubic-bezier(.2,.8,.2,1) forwards;
          transition: transform .22s ease, box-shadow .22s ease, filter .22s ease;
          will-change: transform;
          border-radius: 22px !important;
          min-height: 150px;
          box-shadow: 0 16px 34px rgba(15, 23, 42, .15);
          isolation: isolate;
          cursor: pointer;
        }

        .muhtar-dashboard-card::before {
          content: "";
          position: absolute;
          inset: 0;
          background:
            linear-gradient(135deg, rgba(255,255,255,.22), transparent 38%),
            radial-gradient(circle at top right, rgba(255,255,255,.22), transparent 38%);
          z-index: -1;
        }

        .muhtar-dashboard-card::after {
          content: "";
          position: absolute;
          top: -20%;
          left: -130%;
          width: 82%;
          height: 140%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,.34), transparent);
          transform: skewX(-18deg);
          transition: left .75s ease;
        }

        .muhtar-dashboard-card:hover {
          transform: translateY(-6px) scale(1.015);
          box-shadow: 0 24px 48px rgba(15, 23, 42, .24);
          filter: saturate(1.08);
        }

        .muhtar-dashboard-card:hover::after {
          left: 135%;
        }

        .muhtar-dashboard-card-glow {
          position: absolute;
          right: -30px;
          bottom: -42px;
          width: 130px;
          height: 130px;
          border-radius: 999px;
          background: rgba(255,255,255,.18);
          filter: blur(2px);
          pointer-events: none;
        }

        .muhtar-dashboard-card-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 18px;
        }

        .muhtar-dashboard-icon {
          width: 42px;
          height: 42px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,.18);
          border: 1px solid rgba(255,255,255,.22);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          font-size: 18px;
          box-shadow: inset 0 1px 0 rgba(255,255,255,.22);
        }

        .muhtar-dashboard-status-dot {
          width: 10px;
          height: 10px;
          border-radius: 999px;
          background: rgba(255,255,255,.85);
          box-shadow: 0 0 0 7px rgba(255,255,255,.12);
        }

        .muhtar-dashboard-count {
          text-shadow: 0 8px 24px rgba(0,0,0,.20);
          letter-spacing: -.04em;
        }

        .muhtar-dashboard-label {
          font-weight: 700;
          opacity: .96;
        }

        .muhtar-dashboard-detail {
          width: fit-content;
          opacity: .9;
          font-weight: 800;
          text-underline-offset: 4px;
          transition: opacity .18s ease, transform .18s ease;
        }

        .muhtar-dashboard-detail:hover {
          opacity: 1;
          transform: translateX(4px);
        }

        .muhtar-info-card {
          opacity: 0;
          transform: translateY(12px);
          animation: muhtarListIn .48s ease forwards;
          transition: transform .2s ease, box-shadow .2s ease;
          border-radius: 18px;
        }

        .muhtar-info-card > * {
          border-radius: 18px !important;
          border: 1px solid rgba(226, 232, 240, .95);
          box-shadow: 0 10px 26px rgba(15, 23, 42, .08);
          overflow: hidden;
        }

        .muhtar-info-card:hover {
          transform: translateY(-3px);
        }

        .muhtar-info-card:hover > * {
          box-shadow: 0 18px 36px rgba(15, 23, 42, .13);
        }

        .muhtar-action-buttons {
          animation: muhtarSoftIn .65s ease both;
          padding-bottom: 26px;
        }

        .muhtar-action-buttons ul {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .muhtar-action-buttons li {
          min-width: 190px;
        }

        .muhtar-action-buttons button {
          width: 100%;
          transition: transform .18s ease, box-shadow .18s ease, filter .18s ease;
          border-radius: 14px;
          font-weight: 800;
        }

        .muhtar-action-buttons button:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 25px rgba(15, 23, 42, .18);
          filter: brightness(1.03);
        }

        .muhtar-mobile-top-actions {
          display: none;
        }

        @keyframes muhtarPageFade {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes muhtarTitleIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes muhtarCardIn {
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes muhtarListIn {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes muhtarSoftIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes muhtarPulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.35);
            opacity: .72;
          }
        }

        @keyframes muhtarOrbFloat {
          from {
            transform: translate3d(0, 0, 0) scale(1);
          }
          to {
            transform: translate3d(18px, -12px, 0) scale(1.08);
          }
        }

        @keyframes muhtarRouteChange {
          0% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          45% {
            opacity: .72;
            transform: translateY(4px) scale(.995);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @media (max-width: 1100px) {
          .muhtar-metrics-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .muhtar-module-list {
            grid-template-columns: 1fr;
          }
        }

     @media (max-width: 900px) {
  .muhtar-dashboard-page {
    min-height: 100dvh;
    padding: 10px 14px 34px !important;
    background:
      radial-gradient(circle at 0% 0%, rgba(249, 115, 22, .20), transparent 36%),
      radial-gradient(circle at 100% 0%, rgba(37, 99, 235, .18), transparent 36%),
      linear-gradient(180deg, #eef2f7 0%, #e2e8f0 100%);
  }

  .muhtar-dashboard-app-shell {
    max-width: 430px;
    margin: 0 auto;
    border-radius: 30px;
    padding: 14px;
    background: rgba(255, 255, 255, .62);
    border: 1px solid rgba(255, 255, 255, .78);
    box-shadow: 0 18px 45px rgba(15, 23, 42, .12);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
  }

  .muhtar-dashboard-header {
    display: block;
    margin-bottom: 18px;
    padding: 18px;
    border-radius: 26px;
    background:
      linear-gradient(135deg, rgba(15, 23, 42, .96), rgba(30, 41, 59, .92)),
      radial-gradient(circle at top right, rgba(249, 115, 22, .28), transparent 34%);
    box-shadow: 0 16px 34px rgba(15, 23, 42, .18);
  }

  .muhtar-dashboard-title {
    color: #ffffff;
  }

  .muhtar-dashboard-subtitle {
    color: rgba(226, 232, 240, .92);
    font-size: 13px;
  }

  .muhtar-dashboard-header-right {
    margin-top: 14px;
    justify-content: flex-start;
  }

  .muhtar-dashboard-live-pill {
    background: rgba(255, 255, 255, .12);
    border: 1px solid rgba(255, 255, 255, .14);
    box-shadow: none;
  }

  .muhtar-header-action {
    display: none;
  }

  .muhtar-mobile-top-actions {
    position: relative;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 7px;
    width: fit-content;
    margin: 0 0 10px auto;
    padding: 8px;
    border-radius: 22px;
    background: rgba(15, 23, 42, .96);
    border: 1px solid rgba(255,255,255,.16);
    box-shadow: 0 16px 38px rgba(0,0,0,.30);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    animation: muhtarSoftIn .35s ease both;
  }

  .muhtar-mobile-top-actions button {
    border: 0;
    min-height: 42px;
    padding: 0 12px;
    border-radius: 16px;
    color: white;
    font-size: 12px;
    font-weight: 950;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    cursor: pointer;
    box-shadow: 0 10px 22px rgba(0,0,0,.18);
    transition: transform .16s ease, filter .16s ease;
    white-space: nowrap;
  }

  .muhtar-mobile-top-actions button:active {
    transform: scale(.97);
    filter: brightness(.96);
  }

  .muhtar-mobile-settings {
    background: linear-gradient(135deg, #475569, #334155);
  }

  .muhtar-mobile-support {
    background: linear-gradient(135deg, #16a34a, #15803d);
  }

  .muhtar-mobile-logout {
    background: linear-gradient(135deg, #ef4444, #dc2626);
  }

  .muhtar-mobile-logout-icon {
    width: 44px;
    min-width: 44px;
    padding: 0 !important;
    font-size: 18px !important;
  }

  .muhtar-metrics-grid {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .muhtar-metric-card {
    border-radius: 18px;
    padding: 14px;
  }

  .muhtar-metric-value {
    font-size: 18px;
  }

  .muhtar-metric-title {
    font-size: 11px;
  }

  .muhtar-module-button {
    min-height: 86px;
    grid-template-columns: 44px 1fr;
  }

  .muhtar-module-meta {
    grid-column: 2;
    width: fit-content;
    margin-top: -4px;
  }

  .muhtar-dashboard-card {
    min-height: 138px;
    border-radius: 24px !important;
  }

  .muhtar-dashboard-card:hover {
    transform: translateY(-2px) scale(1.005);
  }

  .muhtar-action-buttons ul {
    display: grid;
    grid-template-columns: 1fr;
  }

  .muhtar-action-buttons li {
    min-width: 0;
  }

  .muhtar-action-buttons button {
    width: 100%;
  }
}

        @media (max-width: 420px) {
          .muhtar-mobile-top-actions {
            right: 8px;
            gap: 5px;
            padding: 7px;
          }

          .muhtar-mobile-top-actions button {
            padding: 0 9px;
            font-size: 11px;
          }

          .muhtar-mobile-top-actions button span:first-child {
            display: none;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .muhtar-dashboard-page,
          .muhtar-dashboard-title,
          .muhtar-dashboard-card,
          .muhtar-section-title,
          .muhtar-info-card,
          .muhtar-action-buttons,
          .muhtar-mobile-top-actions,
          .muhtar-metric-card,
          .muhtar-module-button {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
          }

          .muhtar-dashboard-card,
          .muhtar-info-card,
          .muhtar-action-buttons button,
          .muhtar-mobile-top-actions button,
          .muhtar-metric-card,
          .muhtar-module-button {
            transition: none !important;
          }
        }
      `}</style>

 {/* Mobilde sağ üst resmi alan: alt çıkış kaldırıldı */}
<div className="muhtar-mobile-top-actions">
  <button
    type="button"
    className="muhtar-mobile-settings"
    onClick={() => handleNavigate("#/ayarlar")}
  >
    <span>⚙</span>
    <span>{t("settings")}</span>
  </button>

  <button
    type="button"
    className="muhtar-mobile-support"
    onClick={openWhatsAppSupport}
  >
    <span>☎</span>
    <span>Destek</span>
  </button>

  <button
    type="button"
    className="muhtar-mobile-logout muhtar-mobile-logout-icon"
    onClick={handleLogout}
    title={t("logout")}
  >
    <span>⎋</span>
  </button>
</div>

<div className="muhtar-dashboard-app-shell">
        <div className="muhtar-dashboard-header">
          <div>
            <h1 className="muhtar-dashboard-title text-2xl font-bold mb-0">
              {t("adminPanel")}
            </h1>
            <p className="muhtar-dashboard-subtitle">{t("dashboardSub")}</p>
          </div>

          <div className="muhtar-dashboard-header-right">
            <div className="muhtar-dashboard-live-pill">
              <span />
              {t("activePanel")}
            </div>

            <button
              type="button"
              className="muhtar-header-action muhtar-header-settings"
              onClick={() => handleNavigate("#/ayarlar")}
            >
              ⚙ {t("settings")}
            </button>

            <button
              type="button"
              className="muhtar-header-action muhtar-header-support"
              onClick={openWhatsAppSupport}
            >
              ☎ {t("support")}
            </button>

            <button
              type="button"
              className="muhtar-header-action muhtar-header-logout"
              onClick={handleLogout}
              title={t("safeExit")}
            >
              ⎋ {t("logout")}
            </button>
          </div>
        </div>

        {/* İşletme Durumu */}
        <div className="muhtar-section-head">
          <h2 className="muhtar-section-title text-xl font-semibold">
            {t("businessStatus")}
          </h2>
          <div className="muhtar-section-chip">{t("liveSummary")}</div>
        </div>

        <div className="muhtar-metrics-grid">
          <MetricCard
            title={t("cash")}
            value={formatMoney(metrics.kasaToplam)}
            note={`${metrics.kasaKayit} ${t("cashRecords")} · ${t("todayCash")}: ${formatMoney(metrics.kasaBugun)}`}
            tone="orange"
            delay={40}
          />

          <MetricCard
            title={t("current")}
            value={formatMoney(metrics.cariNet)}
            note={`${t("totalDebt")}: ${formatMoney(metrics.cariToplamBorc)} · ${t("totalReceivable")}: ${formatMoney(metrics.cariToplamAlacak)}`}
            tone="blue"
            delay={90}
          />

          <MetricCard
            title={t("stock")}
            value={metrics.stokKart}
            note={`${t("criticalStock")}: ${metrics.kritikStok} · Değer: ${formatMoney(metrics.stokDeger)}`}
            tone="green"
            delay={140}
          />

          <MetricCard
            title={t("orders")}
            value={metrics.siparisToplam}
            note={`${t("pendingOrders")}: ${metrics.siparisBekleyen} · Ciro: ${formatMoney(metrics.siparisCiro)}`}
            tone="red"
            delay={190}
          />
        </div>

        {/* Modül Geçişleri */}
        <div className="muhtar-section-head">
          <h2 className="muhtar-section-title text-xl font-semibold">
            {t("quickModules")}
          </h2>
          <div className="muhtar-section-chip">{t("appMenu")}</div>
        </div>

        <ul className="muhtar-module-list">
          {quickModules.map((module, index) => (
            <li key={module.title}>
              <button
                type="button"
                className="muhtar-module-button"
                style={{ animationDelay: `${220 + index * 45}ms` }}
                onClick={() =>
                  module.support ? openWhatsAppSupport() : handleNavigate(module.href)
                }
                data-muhtar-nav="true"
              >
                <span className="muhtar-module-icon">{module.icon}</span>

                <span>
                  <span className="muhtar-module-title">{module.title}</span>
                  <span className="muhtar-module-desc">{module.desc}</span>
                </span>

                <span className="muhtar-module-meta">{module.meta}</span>
              </button>
            </li>
          ))}
        </ul>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-10">
          <DashboardCard
            count={anakategoriler.length}
            label={t("donations")}
            color="bg-orange-400"
            href="#/anakategoriler"
            delay={40}
            icon="▦"
          />

          <DashboardCard
            count={kullanicilar.length}
            label={t("users")}
            color="bg-blue-600"
            href="#/kullanicilar"
            delay={90}
            icon="👥"
          />

          <DashboardCard
            count={duyurular.length}
            label={t("announcements")}
            color="bg-purple-600"
            href="#/duyurular"
            delay={140}
            icon="🔔"
          />

          <DashboardCard
            count={etkinlikler.length}
            label={t("events")}
            color="bg-red-600"
            href="#/etkinlikler"
            delay={190}
            icon="📅"
          />

          <DashboardCard
            count={geribildirim.length}
            label={t("news")}
            color="bg-yellow-700"
            href="#/geribildirimler"
            delay={240}
            icon="💬"
          />

          <DashboardCard
            count={faaliyetler.length}
            label={t("activities")}
            color="bg-green-600"
            href="#/faaliyetler"
            delay={290}
            icon="✓"
          />
        </div>

        {/* Son Duyurular */}
        <div className="mb-8">
          <h2 className="muhtar-section-title text-xl font-semibold mb-4">
            {t("recentAnnouncements")}
          </h2>

          <div className="grid gap-4">
            {duyurular.slice(0, 3).map((d, index) => (
              <div
                key={d.id}
                className="muhtar-info-card"
                style={{ animationDelay: `${340 + index * 70}ms` }}
              >
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-bold">{d.baslik}</h3>
                    <p className="text-sm text-gray-600">
                      {d.tarih
                        ? new Date(d.tarih).toLocaleDateString(dateLocale)
                        : ""}
                    </p>
                    <p className="mt-1 text-sm">{d.aciklama}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>

        {/* Yaklaşan Etkinlikler */}
        <div className="mb-8">
          <h2 className="muhtar-section-title text-xl font-semibold mb-4">
            {t("upcomingEvents")}
          </h2>

          <div className="grid gap-4">
            {etkinlikler.slice(0, 3).map((e, index) => (
              <div
                key={e.id}
                className="muhtar-info-card"
                style={{ animationDelay: `${480 + index * 70}ms` }}
              >
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-bold">{e.baslik}</h3>
                    <p className="text-sm text-gray-600">
                      {e.tarih
                        ? new Date(e.tarih).toLocaleDateString(dateLocale)
                        : ""}
                    </p>
                    <p className="mt-1 text-sm">{e.aciklama}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>

        {/* Butonlar li şeklinde */}
        <div className="muhtar-action-buttons">
          <ul>
            <li>
              <Button
                onClick={() => handleNavigate("#/duyurular")}
                data-muhtar-nav="true"
              >
                {t("addAnnouncement")}
              </Button>
            </li>

            <li>
              <Button
                onClick={() => handleNavigate("#/etkinlikler")}
                data-muhtar-nav="true"
              >
                {t("createEvent")}
              </Button>
            </li>

            <li>
              <Button
                onClick={openWhatsAppSupport}
                data-muhtar-nav="true"
              >
                ☎ {t("support")}
              </Button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MuhtarDashboard;